/**
 * THE CALLS THE BOAT MAKES. Arrival is a STATE, and this file is the state.
 *
 * CONTRACT 1.5n(a), 2026-09-03: "THE BOAT MOORS. ARRIVAL IS A STATE, NOT A GEOMETRIC EVENT. When
 * the boat reaches an island it arrives there. The island fills the frame, the player is
 * somewhere, and there is no per-frame intersection test to get wrong."
 *
 * -- WHAT WAS ACTUALLY WRONG, MEASURED ----------------------------------------
 *
 * The owner said "the boat goes right through it". It did. Walking the natural track at 0.5-league
 * steps over its whole 70,728 leagues and asking the renderer's own question at every sample - is
 * this world pixel covered by a sprite whose alpha is at least 128 - the track was inside a drawn
 * island body for 55.5 leagues across NINE of the twenty charted places, and the whole hull was on
 * land for 23.5 of those (tools/world-render/penetration.mjs). Murrin Roost took 21.0 of the 55.5,
 * in one run of 14 leagues and a second of 7.
 *
 * THE "41 STRAIGHT LEAGUES" FIGURE IN THE RULING IS THE BOUNDING BOX, NOT THE ISLAND. Murrin
 * Roost's sprite is 60.7% filled, so its box holds 41 leagues of track where its DRAWN BODY holds
 * 21.25, and the 41 is two passes with 19 leagues of open water between them rather than one
 * straight run. The longest run inside the box is 21 leagues and inside the body 14. Both figures
 * are right about different things and the ruling's conclusion does not move: no placement fixes
 * a 62-league island against a track whose coils are 20.11 leagues apart.
 *
 * -- WHAT A CALL IS -----------------------------------------------------------
 *
 * A closed interval of TRACK DISTANCE. Inside it the boat's natural position is the place's own
 * point and it is made fast there; on the way in and the way out the position eases between the
 * spiral and that point, so the route is continuous and there is no frame where the boat teleports.
 *
 *     enter ......... holdFrom =========== holdTo ......... exit
 *      run in, easing off the spiral     made fast      standing out, easing back on
 *
 * THE INTERVAL IS SOLVED ONCE, when the world is built, from the island's published extent. At
 * run time a resolve costs a binary search over the solved calls and a lerp.
 * There is no intersection test, no silhouette, no per-frame geometry, and nothing that scales
 * with the land pixel count of the current Murrin image. That is what CONTRACT 1.5n(c) means by
 * refusing per-frame collision against a near-zero idle CPU bar.
 *
 * -- WHAT IT DOES NOT TOUCH, WHICH IS THE POINT -------------------------------
 *
 * TRACK DISTANCE IS UNCHANGED. `trackDistanceAtProgress` is not read here and not modified
 * anywhere; a call changes WHERE THE BOAT IS while a stretch of track distance passes, never how
 * much track distance a given progression buys. So discovery, the Melvor cadence, every sighting
 * distance, the charted span, the settled round's pace and every published ETA are bit-for-bit
 * what they were. `calls.test.ts` asserts that against the pre-call layout rather than claiming it.
 *
 * The cost of that choice, stated: the boat's POSITION at a given XP does move, inside a call
 * window and nowhere else, by at most the distance from the spiral to the place point. It is the
 * only pacing consequence and it is bounded by construction.
 */

import { VOYAGE_GEOMETRY } from './constants';
import { coastContains, planCoastPath, coastPathPoint, coastPathHeading, type Coast, type CoastPath } from './coast';
import type { PlaceSeed } from './seed';
import { spiralArcLength, thetaAtArcLength, trackHeadingAtDistance, trackPositionAtDistance } from './track';
import type { Degrees, Leagues, Vec2 } from './units';
import { clamp01, lerpVec2, smoothstep, vec2 } from './units';

const TWO_PI = 2 * Math.PI;
const A = VOYAGE_GEOMETRY.spiralLeaguesPerRadian;

/**
 * The shortest a call can be, in leagues of track. MODELLED at two frame widths - 48 leagues,
 * which at the drift's own measured pace of about 2,100 leagues an hour early in a save is about
 * 82 seconds lying at the island. Short calls are what a berthed island gets: the route runs
 * along its coast rather than through it, so the interval where the body is inside the mooring
 * reach is a couple of leagues, and a couple of leagues is four seconds and is not an arrival.
 */
export const MOORING_CALL_LEAGUES: Leagues = 2 * VOYAGE_GEOMETRY.frameWidthLeagues;

/** Leagues of track spent running in and standing out, at each end of a call. Half a frame. */
export const CALL_APPROACH_LEAGUES: Leagues = VOYAGE_GEOMETRY.frameWidthLeagues / 2;

/**
 * Nearby passes at the same island are one call. The original Murrin crossings were 19 leagues
 * apart. Home's larger fallback body additionally needs one continuous origin call: leaving and
 * interpolating back from a later coil can cross the same body even before that coil reaches it.
 */
export const CALL_MERGE_GAP_LEAGUES: Leagues = 2 * VOYAGE_GEOMETRY.frameWidthLeagues;

/**
 * Arc-length resolution of the solve. One league is a fortieth of a frame width and a fifth of
 * the smallest island the chart carries, so no body can hide between two samples. Every boundary
 * the scan finds is then refined by a FIXED number of bisections, with no convergence test, so the
 * same chart yields the same calls to the last bit on any machine.
 */
export const CALL_SCAN_STEP_LEAGUES: Leagues = 1;
const CALL_REFINE_STEPS = 24;

/**
 * Slack on the radial band each island is scanned in. One frame width, which is far more than the
 * berth clearance and costs nothing: the band is a pruning aid, and being generous with it can
 * only make the scan slower, never make it miss a crossing or a closest approach.
 */
const BAND_MARGIN_LEAGUES: Leagues = VOYAGE_GEOMETRY.frameWidthLeagues;

/** An island as the solve needs it: where the boat lies, and the box the body occupies. */
export interface CallBody {
  readonly placeId: string;
  /** Where the boat is made fast. The place's own point; for an authored island, its anchor. */
  readonly point: Vec2;
  /** Centre of the drawn body, in world leagues. */
  readonly centre: Vec2;
  readonly halfWidthLeagues: Leagues;
  readonly halfHeightLeagues: Leagues;
  /** Conservative navigation silhouette, including whole-hull clearance. */
  readonly coast?: Coast;
}

export interface PlaceCall {
  readonly placeId: string;
  readonly point: Vec2;
  readonly enterLeagues: Leagues;
  readonly holdFromLeagues: Leagues;
  readonly holdToLeagues: Leagues;
  readonly exitLeagues: Leagues;
  /** The track's own heading where the call begins and ends. The boat swings between them. */
  readonly enterHeadingDeg: Degrees;
  readonly exitHeadingDeg: Degrees;
  /** Authored-coast paths are solved once; ordinary call behavior remains exact. */
  readonly approachPath?: CoastPath;
  readonly departurePath?: CoastPath;
  /** Only the origin harbour with multiple body crossings: a precomputed safe departure. */
  readonly departureCurve?: {
    readonly control1: Vec2;
    readonly control2: Vec2;
    readonly end: Vec2;
    readonly initialHeadingDeg: Degrees;
  };
}

/** A world whose places publish no extent makes no calls, and the route is the bare spiral. */
export const NO_CALLS: readonly PlaceCall[] = Object.freeze([]);

// ---------------------------------------------------------------------------
// The solve. Runs once, when the world is built.
// ---------------------------------------------------------------------------

/**
 * SQUARED distance from a point to a box: 0 inside it, the shortest way out squared otherwise.
 *
 * Squared on purpose. The scan asks this 1.4 million times to lay one route - 70,728 samples by
 * twenty islands - and `Math.hypot` there MEASURED 103 ms against 8 ms for the same scan without
 * the square root. Nothing downstream needs the root: the run test is `<= 0` and the reach test
 * compares against a squared standoff.
 */
function boxDistanceSq(px: number, py: number, body: CallBody): number {
  const dx = Math.abs(px - body.centre.x) - body.halfWidthLeagues;
  const dy = Math.abs(py - body.centre.y) - body.halfHeightLeagues;
  if (dx <= 0 && dy <= 0) return 0;
  const ex = dx > 0 ? dx : 0;
  const ey = dy > 0 ? dy : 0;
  return ex * ex + ey * ey;
}

function bodyContains(px: number, py: number, body: CallBody): boolean {
  return body.coast === undefined ? boxDistanceSq(px, py, body) <= 0 : coastContains(body.coast, vec2(px, py));
}

interface Run {
  from: Leagues;
  to: Leagues;
}

/** Bisect the theta at which `inside` flips between two samples. Fixed count, no early exit. */
function refineTheta(lo: number, hi: number, inside: (theta: number) => boolean, wantInsideAtHi: boolean): number {
  let a = lo;
  let b = hi;
  for (let i = 0; i < CALL_REFINE_STEPS; i += 1) {
    const mid = (a + b) / 2;
    if (inside(mid) === wantInsideAtHi) b = mid;
    else a = mid;
  }
  return (a + b) / 2;
}

/**
 * Where the scan stops.
 *
 * The scan walks theta with a step of `CALL_SCAN_STEP_LEAGUES / (A * sqrt(1 + theta^2))`, which is
 * exactly one league of ARC everywhere - a fixed theta step would be 10 leagues at the rim and a
 * millionth of one at home - and it stops at this theta rather than at an arc length, because
 * `spiralArcLength` carries an `asinh` and calling it at every one of 70,728 samples MEASURED
 * 135 ms against 76 ms for the same samples and the same boundaries.
 */
function scanStopTheta(trackLeagues: Leagues): number {
  return thetaAtArcLength(trackLeagues);
}

function mergeRuns(runs: readonly Run[], gap: Leagues): Run[] {
  const out: Run[] = [];
  for (const r of runs) {
    const last = out[out.length - 1];
    if (last !== undefined && r.from - last.to <= gap) last.to = Math.max(last.to, r.to);
    else out.push({ from: r.from, to: r.to });
  }
  return out;
}

export interface SolveCallsOptions {
  /** Leagues of track to solve over. The charted span; past it the round retraces the same track. */
  readonly trackLeagues: Leagues;
  readonly minCallLeagues?: Leagues;
  readonly approachLeagues?: Leagues;
  readonly mergeGapLeagues?: Leagues;
}

/**
 * SOLVE EVERY CALL ON THE ROUTE, sorted by where it begins and guaranteed not to overlap.
 *
 * One scan of the track answers for every island at once, so the cost is one spiral evaluation
 * per league of track rather than one per league per island. A run is a maximal stretch where the
 * spiral is inside a body; two runs at the same island closer than the merge gap are one call; a
 * body the route only passes near contributes its closest approach; and every call is widened to
 * the minimum length and given its run-in and stand-out. Multiple origin-harbour calls retain
 * the first hold and use one precomputed departure curve to the final safe exit. Ordinary call
 * approach lengths and the global merge gap remain unchanged.
 *
 * Where two islands would claim overlapping stretches of track the LATER one is dropped rather
 * than truncated: a boat cannot be made fast at two places, and a truncated call could lose its
 * hold entirely and leave a run-in that eases to nowhere. `calls.test.ts` asserts the shipped
 * table drops none.
 */
export function solveCalls(bodies: readonly CallBody[], options: SolveCallsOptions): PlaceCall[] {
  const trackLeagues = options.trackLeagues;
  if (!Number.isFinite(trackLeagues) || trackLeagues <= 0 || bodies.length === 0) return [];
  const minCall = options.minCallLeagues ?? MOORING_CALL_LEAGUES;
  const approach = options.approachLeagues ?? CALL_APPROACH_LEAGUES;
  const mergeGap = options.mergeGapLeagues ?? CALL_MERGE_GAP_LEAGUES;

  const stopTheta = scanStopTheta(trackLeagues);
  // THE RADIAL BAND EACH ISLAND OCCUPIES. A point inside a box is between the box's nearest and
  // farthest corner distances from home, so a sample whose radius is outside that band, widened by
  // the mooring reach, cannot concern this island at all. It turns the scan from 70,728 samples by
  // twenty islands into 70,728 samples by the one or two whose band they are in: MEASURED 87 ms
  // down to 9 ms for the same boundaries on the shipped chart.
  const bandLo: number[] = [];
  const bandHi: number[] = [];
  for (const b of bodies) {
    const pointR = Math.hypot(b.point.x, b.point.y);
    let near = Math.min(pointR, Math.sqrt(boxDistanceSq(0, 0, b)));
    let far = pointR;
    for (const sx of [-1, 1]) {
      for (const sy of [-1, 1]) {
        const d = Math.hypot(b.centre.x + sx * b.halfWidthLeagues, b.centre.y + sy * b.halfHeightLeagues);
        if (d > far) far = d;
        if (d < near) near = d;
      }
    }
    bandLo.push(Math.max(0, near - BAND_MARGIN_LEAGUES));
    bandHi.push(far + BAND_MARGIN_LEAGUES);
  }
  const runs: Run[][] = bodies.map(() => []);
  const open: (number | null)[] = bodies.map(() => null);
  // The closest the track ever comes to the PLACE POINT, which is where the boat is made fast.
  // Every charted place gets a call there: arrival is reaching the place, not grazing its beach.
  const nearestTheta: number[] = bodies.map(() => 0);
  const nearestDistanceSq: number[] = bodies.map(() => Infinity);
  const insideAt = (theta: number, body: CallBody): boolean => {
    const r = A * theta;
    return bodyContains(r * Math.sin(theta), r * Math.cos(theta), body);
  };
  let previous = 0;

  for (let theta = 0; ; ) {
    const r = A * theta;
    const px = r * Math.sin(theta);
    const py = r * Math.cos(theta);
    for (let i = 0; i < bodies.length; i += 1) {
      if (r < bandLo[i]! || r > bandHi[i]!) continue;
      const body = bodies[i]!;
      const ax = px - body.point.x;
      const ay = py - body.point.y;
      const toPoint = ax * ax + ay * ay;
      if (toPoint < nearestDistanceSq[i]!) {
        nearestDistanceSq[i] = toPoint;
        nearestTheta[i] = theta;
      }
      const inside = bodyContains(px, py, body);
      if (inside && open[i] === null) {
        const edge = theta === previous ? theta : refineTheta(previous, theta, (t) => insideAt(t, body), true);
        open[i] = edge;
      } else if (!inside && open[i] !== null) {
        const edge = refineTheta(previous, theta, (t) => insideAt(t, body), false);
        runs[i]!.push({ from: spiralArcLength(open[i]!), to: spiralArcLength(edge) });
        open[i] = null;
      }
    }
    previous = theta;
    if (theta >= stopTheta) break;
    theta = Math.min(stopTheta, theta + CALL_SCAN_STEP_LEAGUES / (A * Math.sqrt(1 + theta * theta)));
  }
  for (let i = 0; i < bodies.length; i += 1) {
    if (open[i] !== null) runs[i]!.push({ from: spiralArcLength(open[i]!), to: trackLeagues });
  }

  const raw: PlaceCall[] = [];
  for (let i = 0; i < bodies.length; i += 1) {
    const body = bodies[i]!;
    // THE ARRIVAL, always: the instant of closest approach to the place point. A berthed island is
    // deliberately moved clear of the track, so waiting for the route to enter its body would
    // delete the arrival at exactly the nineteen places the placement rule just fixed. Any stretch
    // where the route IS inside the body is folded in beside it, so the boat is made fast for the
    // whole of a crossing it cannot avoid - which is Murrin Roost, and only Murrin Roost.
    const approachStart = spiralArcLength(nearestTheta[i]!);
    const withArrival = [...runs[i]!, { from: approachStart, to: approachStart }].sort((a, b) => a.from - b.from);
    const mine = mergeRuns(withArrival, mergeGap);
    // THE STAND-OUT MUST FIT. A hold that runs to the very end of the charted track has no room to
    // ease back onto the spiral, and the weight then falls from 1 to 0 in one step: MEASURED as a
    // 7.8-league jump at place-19, whose closest approach is the last league of the sea. Clamping
    // the HOLD rather than the exit keeps the ramp, and costs the last place a few leagues of
    // lying time it would only have spent at the rim.
    const lastHold = Math.max(0, trackLeagues - approach);
    const bodyCalls: PlaceCall[] = [];
    for (const run of mine) {
      const mid = (run.from + run.to) / 2;
      const half = Math.max(minCall, run.to - run.from) / 2;
      const holdFrom = Math.max(0, mid - half);
      const holdTo = Math.min(lastHold, mid + half);
      if (!(holdTo > holdFrom)) continue;
      bodyCalls.push({
        placeId: body.placeId,
        point: body.point,
        enterLeagues: Math.max(0, holdFrom - approach),
        holdFromLeagues: holdFrom,
        holdToLeagues: holdTo,
        exitLeagues: Math.min(trackLeagues, holdTo + approach),
        enterHeadingDeg: trackHeadingAtDistance(Math.max(0, holdFrom - approach)),
        exitHeadingDeg: trackHeadingAtDistance(Math.min(trackLeagues, holdTo + approach)),
      });
    }
    // Home is the physical origin harbour. A later run-in lerped through its taller body;
    // holding through all intervening coils instead cost almost ten extra minutes at home.
    // Preserve the first hold, then depart through the verified water corridor to the final
    // safe exit. Controls are solved once; the last tangent matches the spiral at the join.
    if (body.point.x === 0 && body.point.y === 0 && bodyCalls.length > 1) {
      const first = bodyCalls[0]!;
      const last = bodyCalls[bodyCalls.length - 1]!;
      const end = trackPositionAtDistance(last.exitLeagues);
      const dx = end.x - body.point.x;
      const dy = end.y - body.point.y;
      const chord = Math.hypot(dx, dy);
      const reach = Math.min(chord / 3, approach);
      const exitHeading = last.exitHeadingDeg * Math.PI / 180;
      raw.push({ ...first, exitLeagues: last.exitLeagues, exitHeadingDeg: last.exitHeadingDeg,
        departureCurve: {
          control1: vec2(body.point.x + (chord > 0 ? dx / chord * reach : 0), body.point.y + (chord > 0 ? dy / chord * reach : 0)),
          control2: vec2(end.x - Math.sin(exitHeading) * reach, end.y - Math.cos(exitHeading) * reach),
          end,
          initialHeadingDeg: ((Math.atan2(dx, dy) * 180 / Math.PI) + 360) % 360,
        },
      });
    } else if (body.coast !== undefined) {
      for (const call of bodyCalls) raw.push({ ...call,
        approachPath: planCoastPath(trackPositionAtDistance(call.enterLeagues), call.point,
          [body.coast], call.enterHeadingDeg),
        departurePath: planCoastPath(call.point, trackPositionAtDistance(call.exitLeagues),
          [body.coast], undefined, call.exitHeadingDeg),
      });
    } else raw.push(...bodyCalls);
  }

  raw.sort((a, b) => (a.enterLeagues === b.enterLeagues ? (a.placeId < b.placeId ? -1 : 1) : a.enterLeagues - b.enterLeagues));
  const out: PlaceCall[] = [];
  for (const call of raw) {
    const last = out[out.length - 1];
    if (last !== undefined && call.enterLeagues < last.exitLeagues) continue;
    out.push(call);
  }
  return out;
}

/**
 * The bodies a seed table publishes. A seed with no `extent` is a place nobody has drawn, and it
 * contributes no call: the voyage layer refuses to invent a size for an island that does not
 * exist yet, which is the same honesty `PlaceExtent` was added with.
 */
export function callBodiesFor(places: readonly PlaceSeed[]): CallBody[] {
  const out: CallBody[] = [];
  for (const p of places) {
    const e = p.extent;
    if (e === undefined) continue;
    out.push({
      placeId: p.id,
      point: p.position,
      centre: vec2(p.position.x + e.centreOffsetLeagues.x, p.position.y + e.centreOffsetLeagues.y),
      halfWidthLeagues: e.halfWidthLeagues,
      halfHeightLeagues: e.halfHeightLeagues,
      ...(e.coastLeagues === undefined ? {} : { coast: e.coastLeagues.map(v => vec2(p.position.x + v.x, p.position.y + v.y)) }),
    });
  }
  return out;
}

// ---------------------------------------------------------------------------
// The route: the spiral, with the calls in it.
// ---------------------------------------------------------------------------

/** The call covering this track distance, or null. Binary search; the calls do not overlap. */
export function callAt(calls: readonly PlaceCall[], trackDistance: Leagues): PlaceCall | null {
  let lo = 0;
  let hi = calls.length - 1;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    const c = calls[mid]!;
    if (trackDistance < c.enterLeagues) hi = mid - 1;
    else if (trackDistance > c.exitLeagues) lo = mid + 1;
    else return c;
  }
  return null;
}

/** 0 on the spiral, 1 made fast, smooth between. */
export function callHoldWeight(call: PlaceCall, trackDistance: Leagues): number {
  if (trackDistance <= call.enterLeagues || trackDistance >= call.exitLeagues) return 0;
  if (trackDistance >= call.holdFromLeagues && trackDistance <= call.holdToLeagues) return 1;
  if (trackDistance < call.holdFromLeagues) {
    const span = call.holdFromLeagues - call.enterLeagues;
    return span > 0 ? smoothstep((trackDistance - call.enterLeagues) / span) : 1;
  }
  const span = call.exitLeagues - call.holdToLeagues;
  return span > 0 ? smoothstep((call.exitLeagues - trackDistance) / span) : 1;
}

/** Cubic position on the already-solved home departure, with no runtime collision work. */
function departurePoint(call: PlaceCall, t: number): Vec2 {
  const curve = call.departureCurve!;
  const v = 1 - t;
  return vec2(
    v * v * v * call.point.x + 3 * v * v * t * curve.control1.x + 3 * v * t * t * curve.control2.x + t * t * t * curve.end.x,
    v * v * v * call.point.y + 3 * v * v * t * curve.control1.y + 3 * v * t * t * curve.control2.y + t * t * t * curve.end.y,
  );
}

/** WHERE THE BOAT IS: spiral, mooring, ordinary approach, or the solved home departure. */
export function routePositionAt(calls: readonly PlaceCall[], trackDistance: Leagues): Vec2 {
  const call = callAt(calls, trackDistance);
  if (call === null) return trackPositionAtDistance(trackDistance);
  const w = callHoldWeight(call, trackDistance);
  if (w <= 0) return trackPositionAtDistance(trackDistance);
  if (w >= 1) return call.point;
  if (call.approachPath !== undefined && trackDistance < call.holdFromLeagues) return coastPathPoint(call.approachPath, w);
  if (call.departurePath !== undefined && trackDistance > call.holdToLeagues) return coastPathPoint(call.departurePath, 1 - w);
  if (call.departureCurve !== undefined && trackDistance > call.holdToLeagues) return departurePoint(call, 1 - w);
  return lerpVec2(trackPositionAtDistance(trackDistance), call.point, w);
}

/** Shortest-arc interpolation between two compass bearings. */
function swingDeg(from: Degrees, to: Degrees, u: number): Degrees {
  let delta = ((to - from) % 360 + 540) % 360 - 180;
  const d = from + delta * clamp01(u);
  return ((d % 360) + 360) % 360;
}

/**
 * WHICH WAY THE BOAT POINTS on the route. Outside a call it is the track's own closed-form
 * heading. Inside one the boat swings at its mooring, from the heading it came in on to the
 * heading it leaves on, which is the same treatment `settled.ts` gives the come-about at each end
 * of the round: a moored boat swings, it does not hold a bearing. The swing is continuous with the
 * track's heading at both ends of the call by construction.
 */
export function routeHeadingAt(calls: readonly PlaceCall[], trackDistance: Leagues): Degrees {
  const call = callAt(calls, trackDistance);
  if (call === null) return trackHeadingAtDistance(trackDistance);
  if (call.approachPath !== undefined && call.departurePath !== undefined) {
    if (trackDistance < call.holdFromLeagues) return coastPathHeading(call.approachPath, callHoldWeight(call, trackDistance));
    if (trackDistance > call.holdToLeagues) return coastPathHeading(call.departurePath, 1 - callHoldWeight(call, trackDistance));
    const u = (trackDistance - call.holdFromLeagues) / (call.holdToLeagues - call.holdFromLeagues);
    return swingDeg(coastPathHeading(call.approachPath, 1), coastPathHeading(call.departurePath, 0), smoothstep(u));
  }
  if (call.departureCurve !== undefined) {
    const curve = call.departureCurve;
    if (trackDistance <= call.holdToLeagues) {
      const holdSpan = call.holdToLeagues - call.enterLeagues;
      return swingDeg(call.enterHeadingDeg, curve.initialHeadingDeg, smoothstep(holdSpan > 0 ? clamp01((trackDistance - call.enterLeagues) / holdSpan) : 1));
    }
    const t = 1 - callHoldWeight(call, trackDistance);
    const v = 1 - t;
    const dx = 3 * v * v * (curve.control1.x - call.point.x) + 6 * v * t * (curve.control2.x - curve.control1.x) + 3 * t * t * (curve.end.x - curve.control2.x);
    const dy = 3 * v * v * (curve.control1.y - call.point.y) + 6 * v * t * (curve.control2.y - curve.control1.y) + 3 * t * t * (curve.end.y - curve.control2.y);
    return dx === 0 && dy === 0 ? call.exitHeadingDeg : ((Math.atan2(dx, dy) * 180 / Math.PI) + 360) % 360;
  }
  const span = call.exitLeagues - call.enterLeagues;
  const u = span > 0 ? clamp01((trackDistance - call.enterLeagues) / span) : 1;
  return swingDeg(call.enterHeadingDeg, call.exitHeadingDeg, smoothstep(u));
}

/**
 * THE PLACE THE BOAT HAS ARRIVED AT, or null. Set only while it is actually made fast, never
 * during the run-in or the stand-out: a boat still standing in is not at anchor, and the chart's
 * own copy reads `mooredAt` as "made fast".
 */
export function routeCallPlaceIdAt(calls: readonly PlaceCall[], trackDistance: Leagues): string | null {
  const call = callAt(calls, trackDistance);
  if (call === null) return null;
  return callHoldWeight(call, trackDistance) >= 1 ? call.placeId : null;
}

/** Leagues of track the boat spends made fast, over every call. Reported, never asserted on. */
export function totalHoldLeagues(calls: readonly PlaceCall[]): Leagues {
  let sum = 0;
  for (const c of calls) sum += c.holdToLeagues - c.holdFromLeagues;
  return sum;
}

/** The angular span of one coil, published so a test can state the incompatibility in one line. */
export const CALL_COIL_RADIANS = TWO_PI;
